import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Input, Button, List, Avatar, Spin, Skeleton, message } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useParams, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';

const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

const ChatWindow = () => {
    const [input, setInput] = useState('');
    const [messages, setMessages] = useState([{ sender: 'bot', message: 'Hei, ihminen! Luin analyysin, ja nyt olen valmiina auttamaan ja vastaamaan kysymyksiisi siitä.' }]);
    const [isLoading, setIsLoading] = useState(false);
    const [userId, setUserId] = useState(null); // User ID backendistä
    const inputRef = useRef(null);
    const messageListRef = useRef(null);

    const [profileId, setProfileId] = useState(null);
    const [searchParams] = useSearchParams();
    const queryUserId = searchParams.get('userId'); // Hae userId query-parametrista

    useEffect(() => {
        // Lue `userId` ja `profileId` URL:sta
        const fetchedUserId = searchParams.get('userId');
        const fetchedProfileId = searchParams.get('profileId');

        if (fetchedUserId && fetchedProfileId) {
            setUserId(fetchedUserId);
            setProfileId(fetchedProfileId);
        } else {
            console.error('Profiilin ID tai käyttäjän ID puuttuu URL:sta.');
        }
    }, [searchParams]);

    const initializeConversation = async (profileId) => {
        setMessages((prev) => [...prev, { isSkeleton: true }]); // Näytä latausanimaatio

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/start-conversation-with-profile`, {
                profileId, // Lähetetään profiilin ID backendille
                userId: queryUserId, // Query-stringistä otettu käyttäjän ID (jos tarvitaan)
            });

            if (response.data?.userId) {
                setUserId(response.data.userId); // Tallenna backendistä saatu User ID
                setMessages((prev) => [
                    ...prev.slice(0, -1), // Poista Skeleton
                    { sender: 'bot', message: response.data.welcomeMessage || 'Keskustelu aloitettu.' },
                ]);
            }
        } catch (error) {
            console.error("Error initializing conversation:", error.response?.data || error.message);
            setMessages((prev) => [
                ...prev.slice(0, -1),
                { sender: 'bot', message: 'Jokin meni pieleen. Yritä uudelleen.' },
            ]);
        }
    };

    // Vierittää viestilistan automaattisesti uusimman viestin kohdalle
    useEffect(() => {
        if (messageListRef.current) {
            messageListRef.current.scrollTo({
                top: messageListRef.current.scrollHeight,
                behavior: 'smooth',
            });
        }
    }, [messages]);

    // Lähettää heartbeat-signaalin backendille
    useEffect(() => {
        if (!userId) return;

        const intervalId = setInterval(async () => {
            try {
                await axios.post(`${process.env.REACT_APP_API_URL}/api/heartbeat`, { userId });
            } catch (error) {
                console.error('Error sending heartbeat:', error);
            }
        }, 60000);

        return () => clearInterval(intervalId);
    }, [userId]);

    const handleInputChange = (e) => setInput(e.target.value);

    const handleUserResponse = async (event) => {
        event.preventDefault();
        if (!input.trim()) return;

        setIsLoading(true);
        try {
            setMessages((prev) => [...prev, { sender: 'user', message: input }]);
            setMessages((prev) => [...prev, { isSkeleton: true }]); // Näytä latausanimaatio

            const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/v2/user-response`, {
                userId,
                message: input,
            });

            setMessages((prev) => [
                ...prev.slice(0, -1), // Poista Skeleton
                { sender: 'bot', message: response.data.response },
            ]);
        } catch (error) {
            console.error('Failed to process user response:', error);
            setMessages((prev) => [
                ...prev.slice(0, -1),
                { sender: 'bot', message: 'Jokin meni pieleen. Yritä uudelleen.' },
            ]);
        } finally {
            setInput('');
            setIsLoading(false);
            inputRef.current.focus();
        }
    };

    const renderMessageItem = (item) => {
        if (item.isSkeleton) {
            return (
                <List.Item>
                    <Skeleton active paragraph={{ rows: 1 }} avatar />
                </List.Item>
            );
        }

        return (
            <List.Item>
                <List.Item.Meta
                    avatar={
                        <Avatar
                            style={{
                                backgroundColor: item.sender === 'user' ? '#f56a00' : '#87d068',
                            }}
                        >
                            {item.sender === 'user' ? 'YOU' : 'AI'}
                        </Avatar>
                    }
                    title={item.sender === 'user' ? 'Vastauksesi' : 'AI'}
                    description={<ReactMarkdown>{item.message}</ReactMarkdown>}
                />
            </List.Item>
        );
    };

    return (
        <div id="wrap">
            <div className="header">
                <h2>Tovari AI Chat</h2>
            </div>
            <div className="chat-window">
                <div className="message-list" ref={messageListRef}>
                    <List dataSource={messages} renderItem={renderMessageItem} />
                </div>
                <div className="message-field">
                    <Spin spinning={isLoading} indicator={antIcon}>
                        <form onSubmit={handleUserResponse} className="message-form">
                            <Input
                                ref={inputRef}
                                type="text"
                                value={input}
                                onChange={handleInputChange}
                                placeholder="Kirjoita vastauksesi..."
                                onPressEnter={handleUserResponse}
                            />
                            <Button
                                style={{ height: '42px', paddingTop: '12px' }}
                                onClick={handleUserResponse}
                                disabled={!input.trim() || isLoading}
                            >
                                Lähetä viesti
                            </Button>
                        </form>
                    </Spin>
                </div>
            </div>
        </div>
    );
};

export default ChatWindow;
